import React, { PureComponent } from 'react'
import PropTypes from 'prop-types'

import { eventNames, closest, getCursorPosition, vendorPrefix } from './helpers'
import Manager from './Manager'


class DragLayer extends PureComponent {

  static childContextTypes = {
    dnd: PropTypes.shape({
      manager: PropTypes.object,
    }),
  }

  static propTypes = {
    onDragEnd: PropTypes.func,
  }

  static defaultProps = {
    touchDelay: 500,
    mouseDelay: 150,
  }

  manager               = new Manager()
  node                  = null
  activeNode            = null
  initialTouchPosition  = null
  helper                = null
  isMoveInited          = false
  isCursorMoved         = false

  getChildContext() {

    return {
      dnd: {
        manager: this.manager,
      },
    }
  }

  componentDidMount() {
    this.toggleListeners(true)
  }

  componentWillUnmount() {
    this.toggleListeners(false)
  }

  UNSAFE_componentWillUpdate() {
    this.manager.flush()
  }

  toggleListeners(isEnabled) {
    const documentHandlers = {
      move: this.handleDrag,
      end: this.handleTouchEnd,
    }

    const nodeHandlers = {
      start: this.handleTouchStart,
      move: this.handleSort,
    }

    this.applyHandlers(document, documentHandlers, isEnabled)
    this.applyHandlers(this.node, nodeHandlers, isEnabled)
  }

  applyHandlers(node, handlers, isEnabled) {
    const method = isEnabled ? 'addEventListener' : 'removeEventListener'

    Object.keys(handlers).forEach((key) => {
      const params = isEnabled ? { passive: key !== 'move' } : false

      if (eventNames[key]) {
        eventNames[key].forEach((eventName) => {
          if (node[method]) {
            node[method](eventName, handlers[key], params)
          }
        })
      }
    })

    node[method]('scroll', this.cancelDelayedStart, false)
  }

  cancelDelayedStart = () => {
    this.isMoveInited = false
    clearTimeout(this.startTimeout)
  }

  handleTouchStart = (event) => {
    const { isMobile, touchDelay, mouseDelay } = this.props

    const node          = closest(event.target, (node) => node.dndData)
    const isManagerNode = this.manager.nodes.includes(node)
    const isMouseRightButtonClick = event.button === 2

    if (!node || node.dndData.disabled || !isManagerNode || isMouseRightButtonClick) {
      return
    }

    const isTouchStart = event.type === 'touchstart'

    // TODO it can be refactored but now we need this to split click and move events
    // bcs mousemove event always fires — see https://bugs.chromium.org/p/chromium/issues/detail?id=721341
    const delay = isMobile && isTouchStart ? touchDelay : mouseDelay

    this.startTimeout = setTimeout(() => {
      this.isMoveInited = true
    }, delay)

    this.initialTouchPosition = getCursorPosition(event)
    this.activeNode = node

    this.manager.filterItems().forEach((node) => {
      node.initialBounds = node.getBoundingClientRect()
    })
  }

  checkIfCursorMoved = (event) => {
    const { x: currentX, y: currentY } = getCursorPosition(event)
    const { x: initialX, y: initialY } = this.initialTouchPosition

    // set isCursorMoved based on changed coordinates to avoid chrome bug on windows
    // see SCNT-4215 and https://bugs.chromium.org/p/chromium/issues/detail?id=161464
    this.isCursorMoved = currentX !== initialX || currentY !== initialY
  }

  handleDrag = (event) => {
    if (!this.activeNode || !this.isMoveInited) {
      return
    }

    event.preventDefault()
    if (!this.isCursorMoved) {
      this.checkIfCursorMoved(event)
    }

    if (this.isCursorMoved) {
      if (!this.helper) {
        this.createHelper(event)
      }

      this.updateHelperPosition(event)
    }
  }

  handleSort = () => {
    if (!this.activeNode || !this.helper || !this.isMoveInited || !this.isCursorMoved) {
      return
    }

    this.updateNodesPosition()
  }

  handleTouchEnd = () => {
    this.cancelDelayedStart()

    if (!this.activeNode || !this.isCursorMoved) {
      this.clearActive()

      return
    }

    const { onDragEnd } = this.props

    const initialIndex  = this.getInitialIndex(this.manager.nodes)
    const hoveredIndex  = this.getHoveredIndex(this.manager.nodes)

    if (typeof onDragEnd === 'function' && hoveredIndex !== undefined) {
      onDragEnd(initialIndex, hoveredIndex)
    }

    this.clearActive()
  }

  clearActive = () => {
    if (this.activeNode) {
      this.manager.nodes.forEach((node) => {
        node.style[`${vendorPrefix}TransitionDuration`] = ''
        node.style[`${vendorPrefix}Transform`] = ''
      })

      this.activeNode.style.visibility  = ''
      this.activeNode.style.opacity     = ''
    }

    if (this.helper && this.helper.parentNode) {
      this.helper.parentNode.removeChild(this.helper)
    }

    this.isMoveInited   = false
    this.isCursorMoved  = false
    this.activeNode     = null
    this.helper         = null
  }

  createHelper() {
    const bounds  = this.activeNode.getBoundingClientRect()
    const helper  = this.activeNode.cloneNode(true)

    helper.style.position        = 'fixed'
    helper.style.top             = `${bounds.top}px`
    helper.style.left            = `${bounds.left}px`
    helper.style.width           = `${bounds.width}px`
    helper.style.height          = `${bounds.height}px`
    helper.style.boxSizing       = 'border-box'
    helper.style.pointerEvents   = 'none'
    helper.style.zIndex          = 600

    this.activeNode.style.visibility  = 'hidden'
    this.activeNode.opacity           = 0

    this.helper = helper

    document.body.appendChild(this.helper)
  }

  updateHelperPosition(event) {
    const offset = getCursorPosition(event)

    const translate = {
      x: offset.x - this.initialTouchPosition.x,
      y: offset.y - this.initialTouchPosition.y,
    }

    this.helper.style[`${vendorPrefix}Transform`] = `translate3d(${translate.x}px, ${translate.y}px, 0)`
  }

  getInitialIndex(nodes) {
    return this.manager.getNodeIndex(this.activeNode, nodes)
  }

  getHoveredIndex(nodes) {
    if (!this.helper) {
      return
    }

    const helperBounds = this.helper.getBoundingClientRect()
    let hoveredIndex

    nodes.forEach((node, index) => {
      if (node.dndData.disabled) {
        return
      }

      const bounds = node.initialBounds

      const hovered = Boolean(helperBounds && bounds)
        && helperBounds.left > bounds.left - bounds.width / 2
        && helperBounds.left < bounds.left + bounds.width / 2
        && helperBounds.top > bounds.top - bounds.height / 2
        && helperBounds.top < bounds.top + bounds.height / 2

      if (hovered) {
        hoveredIndex = index
      }
    })

    return hoveredIndex
  }

  updateNodesPosition() {
    const nodes         = this.manager.filterItems()
    const initialIndex  = this.getInitialIndex(nodes)
    const hoveredIndex  = this.getHoveredIndex(nodes)

    // if no one node is hovered then return to not lose translates
    if (hoveredIndex === undefined) {
      return
    }

    const delta     = hoveredIndex - initialIndex > 0 ? 1 : -1
    const lowIndex  = delta > 0 ? initialIndex : hoveredIndex
    const highIndex = delta > 0 ? hoveredIndex : initialIndex

    nodes.forEach((node, index) => {
      let transformValue

      if (
        delta > 0 && index > lowIndex && index <= highIndex
        || delta < 0 && index >= lowIndex && index < highIndex
      ) {
        const nodeBounds          = node.initialBounds
        const index               = this.manager.getNodeIndex(node, nodes)
        const closestNodeBounds   = nodes[index - delta].initialBounds

        const translate = {
          x: closestNodeBounds.left - nodeBounds.left,
          y: closestNodeBounds.top - nodeBounds.top,
        }

        transformValue = `translate3d(${translate.x}px, ${translate.y}px, 0)`
      }
      else {
        transformValue = `translate3d(0, 0, 0)`
      }

      node.style[`${vendorPrefix}TransitionDuration`] = `${300}ms`
      node.style[`${vendorPrefix}Transform`] = transformValue
    })
  }

  render() {
    const { children } = this.props

    return React.cloneElement(children, {
      ref: (node) => this.node = node,
    })
  }
}


export default DragLayer
